import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { serviceRegistry } from "../services/core/serviceRegistry";
import { API_BASE_URL } from "../services/common/constants";

// Service imports
import { OrderService } from "../services/admin/services/orderService";
import { MenuCategoryService } from "../services/admin/services/menuCategoryService";
import { MenuItemService } from "../services/admin/services/menuItemService";
import { MenuDigitizationService } from "../services/admin/services/menuDigitizationService";
import { LateCheckoutService } from "../services/admin/services/lateCheckoutService";
import { ServiceRequestService } from "../services/admin/services/serviceRequestService";
import { RoomService } from "../services/admin/services/roomService";
import { HousekeepingService } from "../services/admin/services/housekeepingService";
import { TransferDestinationService } from "../services/admin/services/transferDestinationService";
import { TransferRequestService } from "../services/admin/services/transferRequestService";
import { QrCodeService } from "../services/admin/services/qrCodeService";
import { HotelSettingsService } from "../services/admin/services/hotelSettingsService";
import { StatisticsService } from "../services/admin/services/statisticsService";
import { UserManagementService } from "../services/admin/services/userManagementService";
import { ChatService } from "../services/admin/services/chatService";
import { GuestGuideService } from "../services/admin/services/guestGuideService";
import { QueueService } from "../services/admin/services/queueService";
import { AccountService } from "../services/admin/services/accountService";
import { SeamService } from "../services/admin/services/seamService";
import { DashboardService } from "../services/admin/services/dashboardService";
import { WifiService } from "../services/admin/services/wifiService";
import { BillingService } from "../services/admin/services/billingService";
import { InvoiceSettingsService } from "../services/admin/services/invoiceSettingsService";
import { SearchService } from "../services/admin/services/searchService";
import { CurrencyService } from "../services/admin/services/currencyService";
import { HotelPageSettingsService } from "../services/admin/services/hotelPageSettingsService";
import { PaymentSettingsService } from "../services/admin/services/paymentSettingsService";
import { PaymentsService } from "../services/admin/services/paymentsService";
import { StaffInvitationService } from "../services/admin/services/staffInvitationService";
import { NotificationService } from "../services/admin/services/notificationService";
import { PmsService } from "../services/admin/services/pmsService";
import { OnlineCheckinAdminService } from "../services/admin/services/onlineCheckinAdminService";
import { LanguagesService } from "../services/admin/services/languagesService";
import { ServiceTypeService } from "../services/admin/services/serviceTypeService";
import { CannedResponseService } from "../services/admin/services/cannedResponseService";
import { ChatAttachmentService } from "../services/admin/services/chatAttachmentService";
import { RestaurantSettingsService } from "../services/admin/services/restaurantSettingsService";
import { ModifierService } from "../services/admin/services/modifierService";
import { PromotionService } from "../services/admin/services/promotionService";
import { UpsellService } from "../services/admin/services/upsellService";
import { ServiceCategoryService } from "../services/admin/services/serviceCategoryService";
import { ServiceOptionGroupService } from "../services/admin/services/serviceOptionGroupService";
import { OnboardingService } from "../services/admin/services/onboardingService";
import { LateCheckoutSettingsService } from "../services/admin/services/lateCheckoutSettingsService";
import { LateCheckoutUpsellService } from "../services/admin/services/lateCheckoutUpsellService";
import { SentimentService } from "../services/admin/services/sentimentService";
import { SupportService } from "../services/admin/services/supportService";
import { ReservationService } from "../services/admin/services/reservationService";
import { AdminTermsService } from "../services/admin/services/termsService";
import { HotelAdministrationService } from "../services/admin/services/hotelAdministrationService";
import { UnifiedRequestsService } from "../services/admin/services/unifiedRequestsService";
import { TranslationService } from "../services/admin/services/translationService";
import { GuestDataService } from "../services/admin/services/guestDataService";
import { KitchenDisplayService } from "../services/admin/services/kitchenDisplayService";
import { AuditLogService } from "../services/admin/services/auditLogService";
import { ReservationImportService } from "../services/admin/services/reservationImportService";
import { WorkforceService } from "../services/admin/services/workforceService";
import { ActivityAdminService } from "../services/admin/services/activityAdminService";
import { logger } from "../utils/logger";
import { AxiosInstance } from "axios";

/**
 * Base hook for initializing admin services
 */
export const useAdminServices = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [isInitialized, setIsInitialized] = useState(() =>
    serviceRegistry.isAdminInitialized(),
  );
  const [client, setClient] = useState<AxiosInstance | null>(() => {
    try {
      return serviceRegistry.isAdminInitialized()
        ? serviceRegistry.getAdminClient()
        : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const init = async () => {
      try {
        // Only initialize if not already initialized
        if (!serviceRegistry.isAdminInitialized()) {
          serviceRegistry.initializeAdmin({
            baseUrl: API_BASE_URL,
            getAccessToken: getAccessTokenSilently,
          });
        }
        setIsInitialized(true);
        setClient(serviceRegistry.getAdminClient());
      } catch (error) {
        logger.error("Failed to initialize admin services:", error);
        setIsInitialized(false);
        setClient(null);
      }
    };
    init();
  }, [getAccessTokenSilently]);

  return { isInitialized, client };
};

/**
 * Generic hook factory for creating service-specific hooks
 */
function createServiceHook<T>(ServiceClass: new (client: AxiosInstance) => T) {
  return () => {
    const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
    const [service, setService] = useState<T | null>(null);

    useEffect(() => {
      const initService = async () => {
        // Don't try to initialize if auth is still loading or user is not authenticated
        if (isLoading || !isAuthenticated) {
          setService(null);
          return;
        }

        try {
          // Check if already initialized
          if (!serviceRegistry.isAdminInitialized()) {
            serviceRegistry.initializeAdmin({
              baseUrl: API_BASE_URL,
              getAccessToken: getAccessTokenSilently,
            });
          }

          // Only create a new service instance if one doesn't exist yet.
          // Auth0 silent auth can change getAccessTokenSilently/isAuthenticated references
          // without the underlying axios client changing. Re-creating services would cause
          // cascading re-renders across all 57+ hook consumers.
          setService((prev) => {
            if (prev) return prev;
            const client = serviceRegistry.getAdminClient();
            return new ServiceClass(client);
          });
        } catch (error) {
          logger.error("Failed to initialize service:", error);
          setService(null);
        }
      };

      initService();
    }, [getAccessTokenSilently, isAuthenticated, isLoading]);

    return service;
  };
}

// Service-specific hooks
export const useOrderService = createServiceHook(OrderService);
export const useMenuCategoryService = createServiceHook(MenuCategoryService);
export const useMenuItemService = createServiceHook(MenuItemService);
export const useMenuDigitizationService = createServiceHook(MenuDigitizationService);
export const useLateCheckoutService = createServiceHook(LateCheckoutService);
export const useServiceRequestService = createServiceHook(
  ServiceRequestService,
);
export const useRoomService = createServiceHook(RoomService);
export const useHousekeepingService = createServiceHook(HousekeepingService);
export const useTransferDestinationService = createServiceHook(
  TransferDestinationService,
);
export const useTransferRequestService = createServiceHook(
  TransferRequestService,
);
export const useQrCodeService = createServiceHook(QrCodeService);
export const useHotelSettingsService = createServiceHook(HotelSettingsService);
export const useStatisticsService = createServiceHook(StatisticsService);
export const useUserManagementService = createServiceHook(
  UserManagementService,
);
export const useChatService = createServiceHook(ChatService);
export const useGuestGuideService = createServiceHook(GuestGuideService);
export const useQueueService = createServiceHook(QueueService);
export const useAccountService = createServiceHook(AccountService);
export const useSeamService = createServiceHook(SeamService);
export const useDashboardService = createServiceHook(DashboardService);
export const useWifiService = createServiceHook(WifiService);
export const useBillingService = createServiceHook(BillingService);
export const useInvoiceSettingsService = createServiceHook(
  InvoiceSettingsService,
);
export const useSearchService = createServiceHook(SearchService);
export const useCurrencyService = createServiceHook(CurrencyService);
export const useHotelPageSettingsService = createServiceHook(
  HotelPageSettingsService,
);
// Express Checkout removed: no checkout management service hook
export const usePaymentSettingsService = createServiceHook(
  PaymentSettingsService,
);
export const usePaymentsService = createServiceHook(PaymentsService);
export const useStaffInvitationService = createServiceHook(
  StaffInvitationService,
);
export const useNotificationService = createServiceHook(NotificationService);
export const usePmsService = createServiceHook(PmsService);
export const useOnlineCheckinAdminService = createServiceHook(
  OnlineCheckinAdminService,
);
export const useLanguagesService = createServiceHook(LanguagesService);
export const useServiceTypeService = createServiceHook(ServiceTypeService);
export const useCannedResponseService = createServiceHook(CannedResponseService);
export const useChatAttachmentService = createServiceHook(ChatAttachmentService);
export const useRestaurantSettingsService = createServiceHook(RestaurantSettingsService);
export const useModifierService = createServiceHook(ModifierService);
export const usePromotionService = createServiceHook(PromotionService);
export const useUpsellService = createServiceHook(UpsellService);
export const useServiceCategoryService = createServiceHook(ServiceCategoryService);
export const useServiceOptionGroupService = createServiceHook(ServiceOptionGroupService);
export const useOnboardingService = createServiceHook(OnboardingService);
export const useLateCheckoutSettingsService = createServiceHook(LateCheckoutSettingsService);
export const useLateCheckoutUpsellService = createServiceHook(LateCheckoutUpsellService);
export const useSentimentService = createServiceHook(SentimentService);
export const useSupportService = createServiceHook(SupportService);
export const useReservationService = createServiceHook(ReservationService);
export const useAdminTermsService = createServiceHook(AdminTermsService);
export const useHotelAdministrationService = createServiceHook(HotelAdministrationService);
export const useUnifiedRequestsService = createServiceHook(UnifiedRequestsService);
export const useTranslationService = createServiceHook(TranslationService);
export const useGuestDataService = createServiceHook(GuestDataService);
export const useKitchenDisplayService = createServiceHook(KitchenDisplayService);
export const useAuditLogService = createServiceHook(AuditLogService);
export const useReservationImportService = createServiceHook(ReservationImportService);
export const useWorkforceService = createServiceHook(WorkforceService);
export const useActivityAdminService = createServiceHook(ActivityAdminService);

/**
 * Hook for using multiple services at once
 * @param serviceHooks - Array of service hook functions
 * @returns Object with all requested services
 *
 * @example
 * const { menuCategoryService, menuItemService } = useMultipleServices({
 *   menuCategoryService: useMenuCategoryService,
 *   menuItemService: useMenuItemService
 * });
 */
export function useMultipleServices<T extends Record<string, () => unknown>>(
  serviceHooks: T,
): { [K in keyof T]: ReturnType<T[K]> } {
  const result: Record<string, unknown> = {};

  for (const [key, hook] of Object.entries(serviceHooks)) {
    result[key] = hook();
  }

  return result as { [K in keyof T]: ReturnType<T[K]> };
}
